import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "cropButton", "cropButtonText"]
  static values = {
    mediaId: Number,
    cropUrl: String,
    imageUrl: String
  }

  connect() {
    this.rotation = 0
    this.currentAspectRatio = null
    this.initializeCroppie()
  }

  disconnect() {
    if (this.croppie) {
      this.croppie.destroy()
    }
  }

  initializeCroppie() {
    // Check if Croppie is available
    if (typeof Croppie === 'undefined') {
      setTimeout(() => this.initializeCroppie(), 100)
      return
    }

    this.croppie = new Croppie(this.containerTarget, {
      viewport: { width: 400, height: 400, type: 'square' },
      boundary: { width: 600, height: 600 },
      showZoomer: true,
      enableOrientation: true,
      enableResize: true,
      enableExif: true,
      mouseWheelZoom: 'ctrl'
    })

    this.croppie.bind({
      url: this.imageUrlValue,
      orientation: 1
    })
  }

  setAspectRatio(event) {
    const ratio = event.currentTarget.dataset.ratio
    let width, height

    if (ratio === 'free') {
      width = 400
      height = 400
      this.currentAspectRatio = null
    } else {
      const numericRatio = parseFloat(ratio)
      if (numericRatio >= 1) {
        width = 400
        height = Math.round(400 / numericRatio)
      } else {
        width = Math.round(400 * numericRatio)
        height = 400
      }
      this.currentAspectRatio = numericRatio
    }

    // Destroy and recreate with new viewport
    const currentUrl = this.imageUrlValue
    this.croppie.destroy()

    const viewportType = ratio === '1' ? 'square' : 'square'

    this.croppie = new Croppie(this.containerTarget, {
      viewport: { width, height, type: viewportType },
      boundary: { width: 600, height: 600 },
      showZoomer: true,
      enableOrientation: true,
      enableResize: ratio === 'free',
      enableExif: true,
      mouseWheelZoom: 'ctrl'
    })

    this.croppie.bind({
      url: currentUrl,
      orientation: this.getOrientationValue()
    })

    // Update active button styling
    event.currentTarget.parentElement.querySelectorAll('button').forEach(btn => {
      btn.classList.remove('bg-green-50', 'border-green-500', 'text-green-700')
      btn.classList.add('bg-white', 'border-gray-300', 'text-gray-700')
    })

    event.currentTarget.classList.remove('bg-white', 'border-gray-300', 'text-gray-700')
    event.currentTarget.classList.add('bg-green-50', 'border-green-500', 'text-green-700')
  }

  rotateLeft() {
    this.rotation = (this.rotation - 90) % 360
    this.croppie.rotate(-90)
  }

  rotateRight() {
    this.rotation = (this.rotation + 90) % 360
    this.croppie.rotate(90)
  }

  getOrientationValue() {
    // Convert rotation to EXIF orientation value
    const rotationMap = {
      0: 1,
      90: 6,
      180: 3,
      270: 8,
      '-90': 8,
      '-180': 3,
      '-270': 6
    }
    const normalizedRotation = ((this.rotation % 360) + 360) % 360
    return rotationMap[normalizedRotation] || 1
  }

  zoomIn() {
    const currentZoom = this.croppie.get().zoom || 0
    this.croppie.setZoom(currentZoom + 0.1)
  }

  zoomOut() {
    const currentZoom = this.croppie.get().zoom || 0
    this.croppie.setZoom(Math.max(0, currentZoom - 0.1))
  }

  reset() {
    this.rotation = 0
    this.croppie.bind({
      url: this.imageUrlValue,
      orientation: 1
    })
  }

  async crop() {
    // Disable button and show loading
    this.cropButtonTarget.disabled = true
    this.cropButtonTextTarget.textContent = "Cropping..."

    try {
      // Get the cropped image as a blob
      const blob = await this.croppie.result({
        type: 'blob',
        size: 'original',
        format: 'jpeg',
        quality: 0.9,
        circle: false
      })

      // Create form data and append the blob
      const formData = new FormData()
      formData.append('cropped_image', blob, 'cropped.jpg')

      const response = await fetch(this.cropUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok) {
        // Redirect to the new cropped media
        window.location.href = data.url
      } else {
        alert(`Crop failed: ${data.error || 'Unknown error'}`)
        this.cropButtonTarget.disabled = false
        this.cropButtonTextTarget.textContent = "Crop & Save"
      }
    } catch (error) {
      console.error('Crop error:', error)
      alert('An error occurred while cropping. Please try again.')
      this.cropButtonTarget.disabled = false
      this.cropButtonTextTarget.textContent = "Crop & Save"
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
